// Does this offer reach where the trader asked?
//
// ONE PREDICATE, ONE PLACE (golden rule 12). The trader's offer card, the
// supplier's quote form and the admin console all need to answer the same
// question, and a rule restated per call site is a rule that drifts. Everything
// here is pure — no React, no Supabase — so it can be unit-tested and reused
// on mobile when that session runs.
//
// ── Why a RANK and not a lookup table ───────────────────────────────────────
// requests.delivery_preference is what the trader ASKED for, in their words.
// offers.price_basis is what the supplier PROMISED, in Incoterms. They are not
// two spellings of one thing: they are two ends of a scale measuring HOW FAR
// the supplier carries the goods. Ordering them makes the comparison obvious
// and makes "went further than asked" a different answer from "stopped short",
// which a table of allowed pairs could not express without listing every pair.
//
// ⚠️ FOB DOES NOT SATISFY 'port', AND THIS IS THE WHOLE POINT.
// FOB is the port of LOADING — a port in China. The trader asking for "a Saudi
// port" means the port of ARRIVAL, which is CIF. The two read alike in Arabic
// («الميناء») and a trader comparing an FOB quote against a CIF one sees the
// FOB number as the cheaper of two comparable prices when it is not comparable
// at all: the sea freight is missing from it. This ranking is the only thing
// standing between that trader and a surprise invoice.

/** How far up the chain each Incoterm carries the goods. Higher = further. */
const BASIS_RANK = { EXW: 0, FOB: 1, CIF: 2, DDP: 3 };

/** The minimum rank that satisfies each thing a trader can ask for. */
const PREFERENCE_MIN_RANK = { factory: 0, port: 2, door: 3 };

const norm = (v) => String(v ?? '').trim().toUpperCase();
const normPref = (v) => String(v ?? '').trim().toLowerCase();

/**
 * Compare one offer's price basis against one request's delivery preference.
 *
 * POSITIVE LOOKUP THROUGHOUT (golden rule 12): an unrecognised basis or an
 * unrecognised preference returns 'unknown', never 'short'. A fifth Incoterm
 * or a fourth preference added later must not silently start accusing every
 * supplier who uses it — the safe failure for a flag is not to raise it.
 *
 * @returns {'match'|'exceeds'|'short'|'unknown'}
 *   match    the offer lands exactly where the trader asked
 *   exceeds  the supplier carries it FURTHER than asked (not a problem — a
 *            note at most; they may simply have priced a better service)
 *   short    the offer stops before where the trader asked. THE FLAG.
 *   unknown  we cannot tell: no basis on the offer, no preference on the
 *            request, or a value neither map knows.
 */
export function compareDelivery(priceBasis, deliveryPreference) {
  const basis = BASIS_RANK[norm(priceBasis)];
  const need = PREFERENCE_MIN_RANK[normPref(deliveryPreference)];
  if (basis === undefined || need === undefined) return 'unknown';
  if (basis === need) return 'match';
  return basis > need ? 'exceeds' : 'short';
}

/** True only for the case worth interrupting the trader about. */
export const deliveryFallsShort = (priceBasis, deliveryPreference) =>
  compareDelivery(priceBasis, deliveryPreference) === 'short';

/** Exported for tests and for the supplier-side warning; not for call sites to re-rank. */
export const deliveryRank = (priceBasis) => BASIS_RANK[norm(priceBasis)];

/**
 * The Incoterm a trader's preference maps onto when we need to NAME one —
 * prefilling the supplier's basis, or telling them what was asked for.
 * The inverse of PREFERENCE_MIN_RANK, and deliberately the minimum: a supplier
 * who can do better says so themselves.
 */
export const basisForPreference = (deliveryPreference) =>
  ({ factory: 'EXW', port: 'CIF', door: 'DDP' })[normPref(deliveryPreference)] || null;
